<template>
  <PageContainer maxWidth="1920px">
    <div class="grid">
      <div class="col-12 sm:col sm:flex-grow-0">
        <PageTitle>Campaigns</PageTitle>
      </div>
    </div>
    <Message
      class="mt-0"
      severity="info"
      icon="pi pi-info-circle"
      :closable="false"
      :pt="{
        icon: {
          class: 'mt-1 text-xl align-self-start',
        },
        text: {
          class: 'font-normal text-sm',
        },
      }"
    >
      This page is used to review & approve campaigns before they can go live.
    </Message>
    <DataTable
      class="text-sm mt-5"
      :value="compiledCampaigns"
      size="small"
      stripedRows
      scrollable
      v-model:filters="filters"
      filterDisplay="menu"
      :loading="campaignsStore.campaignsAreLoading"
      paginator
      :rows="20"
      :rowsPerPageOptions="[10, 20, 50]"
    >
      <Column
        field="campaign_name"
        header="Campaign Name"
        frozen
        alignFrozen="left"
        sortable
        filter
      >
        <template #filter="{ filterModel }">
          <InputText
            v-model="filterModel.value"
            type="text"
            class="p-column-filter"
            placeholder="Search by campaign name" />
        </template>
      </Column>
      <Column
        field="org_name"
        header="Advertiser"
        sortable
        filter
      >
        <template #filter="{ filterModel }">
          <InputText
            v-model="filterModel.value"
            type="text"
            class="p-column-filter"
            placeholder="Search by advertiser name" />
        </template>
      </Column>
      <Column
        field="ad_run_slot"
        header="Ad Slot"
        sortable
        filter
        :showFilterMatchModes="false"
      >
        <template #filter="{ filterModel }">
          <Dropdown
            v-model="filterModel.value"
            :options="AD_RUN_SLOT_OPTIONS"
            placeholder="Select One"
            class="p-column-filter"
            style="min-width: 12rem"
            :showClear="true"
          >
              <template #option="slotProps">
                {{  slotProps.option }}
              </template>
          </Dropdown>
      </template>
      </Column>
      <Column
        field="ad_length"
        header="Ad Length"
        sortable
      >
        <template #body="slotProps">
          {{ slotProps.data.ad_length }}s
        </template>
      </Column>
      <Column
        field="bid_cap"
        header="Bid CPM"
        sortable
      >
        <template #body="slotProps">
          {{ slotProps.data.formattedBidCpm }}
        </template>
      </Column>
      <Column
        field="max_budget"
        header="Budget"
        sortable
      >
        <template #body="slotProps">
          {{ slotProps.data.formattedBudget }}
        </template>
      </Column>
      <Column
        field="imp_goal"
        header="Impression Goal"
        sortable
      >
        <template #body="slotProps">
          {{ slotProps.data.formattedImpressions }}
        </template>
      </Column>
      <Column
        field="start_date"
        header="Start Date"
        sortable
      >
        <template #body="slotProps">
          {{ slotProps.data.formattedStartDate }}
        </template>
      </Column>
      <Column
        field="end_date"
        header="End Date"
        sortable
      >
        <template #body="slotProps">
          {{ slotProps.data.formattedEndDate }}
        </template>
      </Column>
      <Column
        field="admin_approval_status"
        header="Status"
        frozen
        alignFrozen="right"
        filter
        :showFilterMatchModes="false"
      >
        <template #body="slotProps">
          <Chip :class="slotProps.data.approvalStatusChip.class">
            {{ slotProps.data.approvalStatusChip.value }}
          </Chip>
        </template>
        <template #filter="{ filterModel }">
          <Dropdown
            v-model="filterModel.value"
            :options="STATUS_OPTIONS"
            placeholder="Select One"
            class="p-column-filter"
            showClear
          >
            <template #option="slotProps">
              {{ slotProps.option }}
            </template>
          </Dropdown>
      </template>
      </Column>
      <Column frozen alignFrozen="right">
        <template #body="slotProps">
          <Button
            v-if="rowOptionsDictionary[slotProps.data.id]
              && rowOptionsDictionary[slotProps.data.id].length > 0"
            icon="pi pi-ellipsis-v"
            text
            plain
            aria-haspopup="true"
            :aria-controls="`row-menu-${slotProps.data.id}`"
            @click="(event) => this.$refs[`rowMenu${slotProps.data.id}`].toggle(event)"
          />
          <Menu
            :id="`row-menu-${slotProps.data.id}`"
            class="right-align-menu"
            :ref="`rowMenu${slotProps.data.id}`"
            popup
            :model="rowOptionsDictionary[slotProps.data.id]"
          />
        </template>
      </Column>

      <template #empty>
        {{  campaignsStore.campaignsAreLoading ? '' : 'No campaigns found' }}
      </template>
      <template #loading>
        Loading...
      </template>
    </DataTable>

    <ConfirmDialog
      v-model:visible="campaignStatusDialog.visible"
      :header="campaignStatusDialog.title"
      @confirm="() => {
        updateCampaignStatus(campaignStatusDialog.campaign, campaignStatusDialog.action)
      }"
      :isSubmitting="campaignStatusDialog.isSubmitting"
    >
      <p>
        {{ campaignStatusDialog.message }}
      </p>
    </ConfirmDialog>
  </PageContainer>
</template>

<script>
import { FilterMatchMode, FilterOperator } from 'primevue/api';
import { useCampaignsStore } from '@/stores';
import { parseMessageFromError } from '@/utils/errors';
import { DateTime } from 'luxon';
import { mapStores } from 'pinia';
import { getCampaignApprovalStatusChip } from '@/utils/statuses';
import ConfirmDialog from '@/components/confirmDialog';
import {
  AD_RUN_SLOT_MID_ROLL_LABEL,
  AD_RUN_SLOT_POST_ROLL_LABEL,
  AD_RUN_SLOT_PRE_ROLL_LABEL,
  CAMPAIGN_APPROVAL_STATUS_APPROVED,
  CAMPAIGN_APPROVAL_STATUS_PENDING,
  CAMPAIGN_APPROVAL_STATUS_REJECTED,
} from '@/constants';
import * as api from '@/api';

const AD_RUN_SLOT_OPTIONS = [
  AD_RUN_SLOT_PRE_ROLL_LABEL,
  AD_RUN_SLOT_MID_ROLL_LABEL,
  AD_RUN_SLOT_POST_ROLL_LABEL,
];
const STATUS_OPTIONS = [
  CAMPAIGN_APPROVAL_STATUS_PENDING,
  CAMPAIGN_APPROVAL_STATUS_APPROVED,
  CAMPAIGN_APPROVAL_STATUS_REJECTED,
];

export default {
  components: {
    ConfirmDialog,
  },
  computed: {
    ...mapStores(
      useCampaignsStore,
    ),
    compiledCampaigns() {
      return this.campaignsStore.campaigns.map((item) => {
        const formattedBidCpm = !Number.isNaN(Number(item.bid_cap))
          ? Number(item.bid_cap).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            style: 'currency',
            currency: 'USD',
          })
          : '$0';
        const formattedBudget = !Number.isNaN(Number(item.max_budget))
          ? Number(item.max_budget).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            style: 'currency',
            currency: 'USD',
          })
          : '$0';
        const formattedImpressions = !Number.isNaN(Number(item.imp_goal))
          ? Number(item.imp_goal).toLocaleString('en-US', {
            minimumFractionDigits: 0,
          })
          : 0;
        const formattedStartDate = DateTime.fromISO(item.start_date)
          .toLocaleString(DateTime.DATE_MED);
        const formattedEndDate = DateTime.fromISO(item.end_date)
          .toLocaleString(DateTime.DATE_MED);

        const approvalStatusChip = getCampaignApprovalStatusChip(item.admin_approval_status);

        return {
          ...item,
          formattedBidCpm,
          formattedBudget,
          formattedImpressions,
          formattedStartDate,
          formattedEndDate,
          approvalStatusChip,
        };
      });
    },
    rowOptionsDictionary() {
      return this.compiledCampaigns.reduce((acc, item) => {
        if (item.admin_approval_status === CAMPAIGN_APPROVAL_STATUS_PENDING) {
          acc[item.id] = [
            {
              label: 'Approve',
              command: () => {
                this.campaignStatusDialog.visible = true;
                this.campaignStatusDialog.campaign = item;
                this.campaignStatusDialog.title = 'Approve campaign';
                this.campaignStatusDialog.message = 'Are you sure you want to approve this campaign? Once complete, users will be able to approve their generated ads.';
                this.campaignStatusDialog.action = CAMPAIGN_APPROVAL_STATUS_APPROVED;
              },
            },
            {
              label: 'Reject',
              command: () => {
                this.campaignStatusDialog.visible = true;
                this.campaignStatusDialog.campaign = item;
                this.campaignStatusDialog.title = 'Reject campaign';
                this.campaignStatusDialog.message = 'Are you sure you want to reject this campaign?';
                this.campaignStatusDialog.action = CAMPAIGN_APPROVAL_STATUS_REJECTED;
              },
            },
          ];
        }

        return acc;
      }, {});
    },
  },
  data() {
    return {
      AD_RUN_SLOT_OPTIONS,
      STATUS_OPTIONS,
      filters: {
        campaign_name: {
          operator: FilterOperator.AND,
          constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
        },
        org_name: {
          operator: FilterOperator.AND,
          constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
        },
        ad_run_slot: {
          value: null,
          matchMode: FilterMatchMode.EQUALS,
        },
        admin_approval_status: {
          value: CAMPAIGN_APPROVAL_STATUS_PENDING,
          matchMode: FilterMatchMode.EQUALS,
        },
      },
      campaignStatusDialog: {
        visible: false,
        isSubmitting: false,
        campaign: null,
      },
    };
  },
  mounted() {
    this.getCampaigns();
  },
  methods: {
    async getCampaigns() {
      try {
        await this.campaignsStore.getCampaigns();
      } catch (error) {
        const message = parseMessageFromError(error, 'Error loading campaign.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      }
    },
    async updateCampaignStatus(campaign, newStatus) {
      try {
        this.campaignStatusDialog.isSubmitting = true;

        await api.setAdminApproval({
          campaignId: campaign.id,
          status: newStatus,
        });

        this.$toast.add({
          severity: 'success',
          detail: `Campaign approval status has been set to ${newStatus}`,
        });
        this.campaignStatusDialog.visible = false;

        this.getCampaigns();
      } catch (error) {
        const message = parseMessageFromError(error, 'Error updating campaign status.');

        this.$toast.add({
          severity: 'error',
          detail: message,
        });
      } finally {
        this.campaignStatusDialog.isSubmitting = false;
      }
    },
  },
};
</script>
